import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Zap,
  MessageSquare,
  AlertTriangle,
  TrendingUp,
  Building2,
  Sparkles,
  ClipboardList,
  TableProperties,
  ChevronRight,
} from "lucide-react";

interface SectionDef {
  id: string;
  number: string;
  label: string;
  desc: string;
  icon: React.ComponentType<{ size?: number; className?: string; color?: string }>;
  color: string;
}

const SECTIONS: SectionDef[] = [
  {
    id: "section-social-kpis",
    number: "01",
    label: "En diez segundos",
    desc: "Métricas y KPIs ejecutivos globales",
    icon: Zap,
    color: "#f59e0b",
  },
  {
    id: "section-social-voice",
    number: "02",
    label: "La voz de la comunidad",
    desc: "Reseñas recientes y testimonios",
    icon: MessageSquare,
    color: "#38bdf8",
  },
  {
    id: "section-social-immediate",
    number: "03",
    label: "Acción inmediata",
    desc: "Alertas críticas y casos urgentes",
    icon: AlertTriangle,
    color: "#ff665e",
  },
  {
    id: "section-social-trend",
    number: "04",
    label: "Tendencia de notas e hitos",
    desc: "Evolución 12 meses y banderas 🚩",
    icon: TrendingUp,
    color: "#4285f4",
  },
  {
    id: "section-social-diagnosis",
    number: "05",
    label: "Diagnóstico de sedes",
    desc: "Aspectos críticos y comparativas",
    icon: Building2,
    color: "#a855f7",
  },
  {
    id: "section-social-opportunity",
    number: "06",
    label: "Oportunidad y elogios",
    desc: "Temas elogiados y prueba social",
    icon: Sparkles,
    color: "#34a853",
  },
  {
    id: "section-social-plan",
    number: "07",
    label: "Plan de acción sugerido",
    desc: "Tareas correctivas y foco por área",
    icon: ClipboardList,
    color: "#06b6d4",
  },
  {
    id: "section-social-comments-table",
    number: "08",
    label: "Registro de comentarios",
    desc: "Feed detallado con filtros y respuestas",
    icon: TableProperties,
    color: "var(--blue, #7d96ff)",
  },
];

export function SocialMobileNavDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string>("section-social-kpis");

  // IntersectionObserver para detectar qué sección está visible en pantalla
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
            break;
          }
        }
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0.1,
      }
    );

    for (const sec of SECTIONS) {
      const el = document.getElementById(sec.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const handleScrollTo = (id: string) => {
    setIsOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  };

  const activeSec = SECTIONS.find((s) => s.id === activeSectionId) || SECTIONS[0];

  return (
    <>
      {/* Botón flotante Mobile-First en la esquina inferior derecha o centro */}
      <div className="social-mobile-nav-trigger-wrap">
        <button
          type="button"
          className="social-mobile-nav-pill"
          onClick={() => setIsOpen(true)}
          aria-label="Abrir menú de secciones"
        >
          <Menu size={16} />
          <span className="social-mobile-nav-pill-title">
            <strong>{activeSec.number}</strong> {activeSec.label}
          </span>
          <span className="social-mobile-nav-badge">Secciones</span>
        </button>
      </div>

      {/* Drawer lateral / Bottom-sheet móvil */}
      {isOpen && (
        <div className="social-mobile-drawer-overlay" onClick={() => setIsOpen(false)}>
          <div
            className="social-mobile-drawer-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Navegación de secciones de Social"
          >
            {/* Header del drawer */}
            <div className="social-mobile-drawer-header">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--blue, #7d96ff)" }} />
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text)" }}>
                  Módulo Social & Reseñas
                </h3>
              </div>
              <button
                type="button"
                className="social-mobile-drawer-close"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar navegación"
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: "0 0 14px 0", fontSize: "12px", color: "var(--muted)", padding: "0 18px" }}>
              Navega directamente a cualquier sección ejecutiva o a la tabla de comentarios:
            </p>

            {/* Lista de Secciones con feedback táctil */}
            <div className="social-mobile-drawer-list">
              {SECTIONS.map((sec) => {
                const Icon = sec.icon;
                const isActive = sec.id === activeSectionId;
                return (
                  <button
                    key={sec.id}
                    type="button"
                    className={`social-mobile-drawer-item ${isActive ? "is-active" : ""}`}
                    onClick={() => handleScrollTo(sec.id)}
                  >
                    <div
                      className="social-mobile-drawer-icon"
                      style={{
                        background: `color-mix(in srgb, ${sec.color} 14%, transparent)`,
                        color: sec.color,
                      }}
                    >
                      <Icon size={16} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 750, color: sec.color }}>
                          {sec.number}
                        </span>
                        <span style={{ fontSize: "13px", fontWeight: 650, color: "var(--text)" }}>
                          {sec.label}
                        </span>
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "2px" }}>
                        {sec.desc}
                      </div>
                    </div>

                    <ChevronRight size={15} color="var(--dim)" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
